import { Widget } from "../types";
import { IUser, IRole } from "@/modules/auth/types";

/**
 * Check if user has the dashboard_administrator permission (superadmin)
 */
export function isSuperAdmin(user: IUser): boolean {
    return user.roles?.some((role: IRole) => role.permissions?.some((p) => p.name === "ai_dashboard_administrator")) ?? false;
}

/**
 * Check if user is internal (has any role with type INTERNAL)
 */
export function isInternalUser(user: IUser): boolean {
    return user.roles?.some((role: IRole) => role.type === "INTERNAL") ?? false;
}

/**
 * Check if a widget is visible to the user
 * Rules:
 * - Widget is visible if publishedToAll is true
 * - Widget is visible if user created it
 * - Superadmin can see all widgets
 */
export function canViewWidget(widget: Widget | null | undefined, user: IUser | null): boolean {
    if (!user || !widget) {
        return false;
    }

    // Superadmin can see all widgets
    if (isSuperAdmin(user)) {
        return true;
    }

    // Published widgets are visible to everyone
    if (widget.publishedToAll) {
        return true;
    }

    // User can see their own widgets
    if (widget.createdBy && String(widget.createdBy) === user.userId) {
        return true;
    }

    return false;
}

/**
 * Check if a user can edit a widget
 * Rules:
 * - User can edit if they created the widget (createdBy === user.userId)
 * - Superadmin can edit any widget
 */
export function canEditWidget(widget: Widget | null | undefined, user: IUser | null): boolean {
    if (!user || !widget) {
        return false;
    }

    // Superadmin can edit any widget
    if (isSuperAdmin(user)) {
        return true;
    }

    // User can edit their own widgets
    if (widget.createdBy && String(widget.createdBy) === user.userId) {
        return true;
    }

    return false;
}

/**
 * Check if a user can delete a widget
 * Same rules as edit
 */
export function canDeleteWidget(widget: Widget | null | undefined, user: IUser | null): boolean {
    if (!user || !widget) {
        return false;
    }

    // Superadmin can edit any manually created widget
    if (isSuperAdmin(user)) {
        return true;
    }

    // User can edit their own widgets
    if (widget.createdBy && String(widget.createdBy) === user.userId) {
        return true;
    }

    return false;
}

/**
 * Check if a user can duplicate a widget
 * Rules:
 * - Any authenticated user can duplicate any widget
 */
export function canDuplicateWidget(widget: Widget | null | undefined, user: IUser | null): boolean {
    if (!user || !widget) {
        return false;
    }

    if (["consumer-data", "data-sources", "iframe"].includes(widget.type)) {
        return false;
    }

    return true;
}

/**
 * Get reason why user cannot edit widget
 */
export function getEditDisabledReason(widget: Widget | null | undefined, user: IUser | null): string | null {
    if (!user) {
        return "You must be logged in to edit widgets";
    }

    if (!widget) {
        return null;
    }

    if (widget.createdBy && String(widget.createdBy) !== user.userId && !isSuperAdmin(user)) {
        return "You can only edit widgets you created";
    }

    return null;
}

/**
 * Check if user can publish widgets/pages globally
 */
export function canPublishGlobally(user: IUser | null): boolean {
    if (!user) {
        return false;
    }
    return isSuperAdmin(user);
}

/**
 * Whether a widget is a "custom" widget (DB tinyint `custom = 1`).
 * Custom widgets cannot be shared.
 */
export function isCustomWidget(widget: Widget | null | undefined): boolean {
    return Boolean(widget?.custom);
}

/**
 * Check if a user can share a widget
 * Rules:
 * - User needs the ai_dashboard_share_widget permission
 * - Custom widgets (custom = 1) cannot be shared
 */
export function canShareWidget(widget: Widget | null | undefined, user: IUser | null): boolean {
    if (!user) {
        return false;
    }
    // Custom widgets are not shareable
    if (isCustomWidget(widget)) {
        return false;
    }
    return user.roles?.some((role: IRole) => role.permissions?.some((p) => p.name === "ai_dashboard_share_widget")) ?? false;
}

/**
 * Check if user can publish AI insights
 * User needs ai_insights_publisher permission
 */
export function canPublishInsights(user: IUser | null): boolean {
    if (!user) {
        return false;
    }

    return user.roles?.some((role: IRole) => role.permissions?.some((p) => p.name === "ai_insights_publisher")) ?? false;
}


/**
 * Check if user has a specific permission by name
 */
export function hasPermission(user: IUser | null, permissionName: string): boolean {
    if (!user) return false;
    return user.roles?.some((role: IRole) => role.permissions?.some((p) => p.name === permissionName)) ?? false;
}

/**
 * Check if user can use the hide no-data widgets switch
 */
export function canHideNoDataWidgets(user: IUser | null): boolean {
    return hasPermission(user, 'ai_dashboard_hide_no_data_widgets_switch');
}

/**
 * Check if AI insights should be shown for a widget.
 * Each flag independently grants access (OR semantics):
 *  - publishInsights        → all users (internal + external)
 *  - enableAIInsights       → all internal users
 *  - enableSuperUserInsights → internal users with ai_insights_super_user permission only
 */
export function canViewAIInsights(widget: Widget, user: IUser | null): boolean {
    if (!user) return false;

    if (widget.metadata?.publishInsights) return true;

    const internal = isInternalUser(user);

    if (widget.metadata?.enableAIInsights && internal) return true;

    if (widget.metadata?.enableSuperUserInsights && internal && hasPermission(user, "ai_insights_super_user")) return true;

    return false;
}

/**
 * Check if AI recommendations should be shown for a widget.
 * Same shape as canViewAIInsights, with its own set of metadata flags so insights and
 * recommendations can be published independently of each other - but the super-user tier
 * reuses the same ai_insights_super_user permission (same cohort of elevated users for
 * both kinds of AI-generated widget content, no need for a second permission):
 *  - publishRecommendations        → all users (internal + external)
 *  - enableAIRecommendations       → all internal users
 *  - enableSuperUserRecommendations → internal users with ai_insights_super_user permission only
 */
export function canViewAIRecommendations(widget: Widget, user: IUser | null): boolean {
    if (!user) return false;

    if (widget.metadata?.publishRecommendations) return true;

    const internal = isInternalUser(user);

    if (widget.metadata?.enableAIRecommendations && internal) return true;

    if (widget.metadata?.enableSuperUserRecommendations && internal && hasPermission(user, "ai_insights_super_user")) return true;

    return false;
}
